<template>
  <div class="mb-6">
    <PasswordHelperGraphic
      class="mt-9 mb-8"
      :newPassword="newPassword"
      :confirmPassword="confirmPassword"
      :currentPassword="currentPassword"
      :includeCurrentPassword="includeCurrentPassword"
    />
    <slot></slot>
    <IonList inset>
      <StackedTextInput
        v-if="includeCurrentPassword"
        title="Current Password"
        type="password"
        v-model="currentPassword.value"
        :errors="currentPassword.errors"
        :state="currentPassword.state"
        gray
      />
      <StackedTextInput
        title="New Password"
        type="password"
        v-model="newPassword.value"
        :errors="newPassword.errors"
        :state="newPassword.state"
        gray
      />
      <StackedTextInput
        title="Confirm Password"
        type="password"
        v-model="confirmPassword.value"
        :errors="confirmPassword.errors"
        :state="confirmPassword.state"
        gray
      />
    </IonList>
  </div>
</template>

<script setup lang="ts">
import { onIonViewWillLeave, IonList } from '@ionic/vue';
import PasswordHelperGraphic from '@/components/user-account/PasswordHelperGraphic.vue';
import StackedTextInput from '@/components/forms/stacked/StackedTextInput.vue';
import { watch, watchEffect } from 'vue';
import { UserService } from '@/http-services/user.service';
import { AsyncFormControlValidator, useFormControl } from '@/composables/use-form-control';

export interface IPasswordEmit {
  valid: boolean;
  currentPassword?: string;
  newPassword: string;
  confirmPassword: string;
}

const emit = defineEmits<{
  (e: 'onPassword', password: IPasswordEmit): void;
}>();
const userService = new UserService();
const newPassword = useFormControl(() => '', {
  asyncValidators: [NewPasswordValidator()],
});
const confirmPassword = useFormControl(() => '', {
  asyncValidators: [ConfirmPasswordValidator()],
});
const currentPassword = useFormControl(() => '', {
  asyncValidators: [CurrentPasswordValidator()],
});
const props = withDefaults(defineProps<{ includeCurrentPassword?: boolean; token?: string }>(), {
  includeCurrentPassword: false,
});

watch(() => newPassword.value, () => {
  confirmPassword.updateValidity()
})

function NewPasswordValidator(): AsyncFormControlValidator {
  return async (val: string, signal) => {
    const errors = await userService.validatePassword({
      currentPassword: currentPassword.value,
      newPassword: val,
      confirmPassword: confirmPassword.value,
      token: props.token,
    }, signal);
    const i = errors.filter((f) => f.path === 'newPassword');
    return { valid: i.length ? false : true, errors: i };
  };
}
function ConfirmPasswordValidator(): AsyncFormControlValidator {
  return async (val: string, signal) => {
    const errors = await userService.validatePassword({
      currentPassword: currentPassword.value,
      newPassword: newPassword.value,
      confirmPassword: val,
      token: props.token,
    }, signal);
    const i = errors.filter((f) => f.path === 'confirmPassword');
    return { valid: i.length ? false : true, errors: i };
  };
}
function CurrentPasswordValidator(): AsyncFormControlValidator {
  return async (val: string, signal) => {
    if (!props.includeCurrentPassword) {
      return { valid: true, errors: [] };
    }
    const errors = await userService.validatePassword({
      currentPassword: val,
      newPassword: newPassword.value,
      confirmPassword: confirmPassword.value,
      token: props.token,
    }, signal);
    const i = errors.filter((f) => f.path === 'currentPassword');
    return { valid: i.length ? false : true, errors: i };
  };
}

watchEffect(() => {
  const current = props.includeCurrentPassword ? currentPassword.value && currentPassword.valid : true;
  const updated = newPassword.value && newPassword.valid;
  const confirm = confirmPassword.value && confirmPassword.valid;
  emit('onPassword', {
    valid: !!(current && updated && confirm),
    newPassword: newPassword.value,
    confirmPassword: confirmPassword.value,
    currentPassword: currentPassword.value,
  });
});

onIonViewWillLeave(() => {
  newPassword.value = '';
  confirmPassword.value = '';
  currentPassword.value = undefined;
});
</script>
